import React from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';

const BannerContainer = styled(Box)({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: '#000',
  color: '#fff',
  padding: '16px',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  boxShadow: '0 -4px 12px rgba(0,0,0,0.15)',
  borderRadius: 0,
  borderTop: '2px solid #fff'
});

const AcceptButton = styled(Button)({
  backgroundColor: '#fff',
  color: '#000',
  fontWeight: 700,
  textTransform: 'none',
  padding: '10px 30px',
  borderRadius: 0,
  minWidth: '140px',
  '&:hover': {
    backgroundColor: '#e0e0e0',
  }
});

const LinkText = styled('a')({
  color: '#fff',
  textDecoration: 'underline',
  '&:hover': {
    color: '#e0e0e0',
  }
});

const CookieBanner = ({ onAccept }) => {
  const [isVisible, setIsVisible] = React.useState(() => {
    const accepted = localStorage.getItem('cookiesAccepted');
    const dismissed = sessionStorage.getItem('cookieBannerDismissed');
    return !accepted && !dismissed;
  });

  const [expanded, setExpanded] = React.useState(false);

  const handleAccept = () => {
    localStorage.setItem('cookiesAccepted', 'true');
    setIsVisible(false);
    onAccept();
  };

  const handleClose = () => {
    setIsVisible(false);
    sessionStorage.setItem('cookieBannerDismissed', 'true');
  };

  if (!isVisible) return null;

  return (
    <BannerContainer>
      <Box sx={{ flex: 1, mr: 2 }}>
        <Typography sx={{ 
          fontSize: '14px',
          fontWeight: 500,
          mb: 1
        }}>
          🍪 Your Privacy Choices Matter
        </Typography>
        
        <Typography sx={{ 
          fontSize: '14px',
          fontWeight: 400,
          mb: expanded ? 2 : 0,
          '& a': {
            color: 'inherit'
          }
        }}>
          We value your privacy and want to be transparent about how we use cookies and similar technologies on our website.
          {!expanded ? (
            <Button 
              onClick={() => setExpanded(true)}
              sx={{ 
                color: '#fff',
                textDecoration: 'underline',
                padding: '0 4px',
                minWidth: 'auto',
                '&:hover': {
                  background: 'none',
                  opacity: 0.8
                }
              }}
            >
              Learn more
            </Button>
          ) : (
            <>
              <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                <li>Essential Cookies: Required for basic website functionality</li>
                <li>Analytics Cookies: Help us understand how you use our site</li>
                <li>Marketing Cookies: Enable personalized content and ads</li>
                <li>Preference Cookies: Remember your settings and choices</li>
              </Box>
              We collect and process information in accordance with our{' '}
              <LinkText href="/privacy-policy">Privacy Policy</LinkText>. To learn more about how we use cookies or to change your preferences, please read our{' '}
              <LinkText href="/cookie-policy">Cookie Policy</LinkText>. You can manage your preferences at any time by visiting our{' '}
              <LinkText href="/cookie-settings">Cookie Settings</LinkText>.
              <br />
              For more information on how we protect your personal data, please refer to our{' '}
              <LinkText href="/data-protection-policy">Data Protection Policy</LinkText>.
              <Button 
                onClick={() => setExpanded(false)}
                sx={{ 
                  color: '#fff',
                  textDecoration: 'underline',
                  padding: '0 4px',
                  minWidth: 'auto',
                  '&:hover': {
                    background: 'none',
                    opacity: 0.8
                  }
                }}
              >
                Show less
              </Button>
            </>
          )}
        </Typography>
      </Box>
      
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' }, 
        alignItems: 'center', 
        gap: 1,
        minWidth: { xs: '100%', sm: 'auto' }
      }}>
        <AcceptButton 
          onClick={handleAccept}
          startIcon={<span role="img" aria-label="cookie">🍪</span>}
        >
          Accept All Cookies
        </AcceptButton>
        <Button
          variant="outlined"
          onClick={() => window.location.href = '/cookie-settings'}
          sx={{
            color: '#fff',
            borderColor: '#fff',
            textTransform: 'none',
            padding: '8px 24px',
            minWidth: { xs: '100%', sm: '120px' },
            '&:hover': {
              borderColor: '#e0e0e0',
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          Manage Preferences
        </Button>
        <IconButton 
          onClick={handleClose}
          aria-label="Temporarily dismiss cookie banner"
          title="Dismiss (Cookie banner will show again in next session)"
          sx={{ 
            color: '#fff',
            padding: '8px',
            alignSelf: { xs: 'flex-end', sm: 'center' },
            position: { xs: 'absolute', sm: 'relative' },
            top: { xs: '8px', sm: 'auto' },
            right: { xs: '8px', sm: 'auto' },
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </BannerContainer>
  );
};

export default CookieBanner; 